

import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_URL, SITE_DOMAIN } from '@/lib/constants/urls';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  keywords?: string;
  author?: string;
  structuredData?: Record<string, any> | Record<string, any>[];
  nextPageUrl?: string;
  prevPageUrl?: string;
  noindex?: boolean;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'PaperVerse Reviews - Research Paper Review Platform',
  description = 'PaperVerse - Academic research collaboration platform for paper reviews, version control, and peer feedback.',
  canonicalUrl = SITE_URL,
  ogType = 'website',
  ogImage = '/opengraph_image.png',
  keywords = 'AI paper review, research paper review, peer review platform, academic collaboration, version control, AI research feedback',
  author = 'PaperVerse Reviews',
  structuredData,
  nextPageUrl,
  prevPageUrl,
  noindex = false,
}) => {
  // Normalize canonical URL to prevent crawler issues
  const normalizeUrl = (url: string): string => {
    let normalized = url.replace(/\/indexhtml$/, '').replace(/\/index\.html$/, '');
    if (normalized === SITE_URL) {
      normalized = `${SITE_URL}/`;
    }
    normalized = normalized.replace(/([^:]\/)\/+/g, '$1');
    return normalized;
  };

  const normalizedCanonicalUrl = normalizeUrl(canonicalUrl);

  // Resolve OG image to absolute URL when it's a relative path
  const resolvedOgImage = ogImage.startsWith('http')
    ? ogImage
    : `${SITE_URL}${ogImage.startsWith('/') ? '' : '/'}${ogImage}`;

  const formatTitle = (inputTitle: string) => {
    if (inputTitle.toLowerCase().includes('paperverse')) {
      return inputTitle;
    }
    if (inputTitle.includes(' - ') || inputTitle.includes(' | ')) {
      return inputTitle;
    }
    return `${inputTitle} | PaperVerse Reviews`;
  };

  const fullTitle = formatTitle(title);

  // Extract academic citation meta tags from ScholarlyArticle structured data
  const isAcademicPaper = structuredData && Array.isArray(structuredData) &&
    structuredData.some(item => item['@type'] === 'ScholarlyArticle');

  let paperAuthors: string[] = [];
  let publicationYear = '';
  let originalPublisher = '';

  if (isAcademicPaper && Array.isArray(structuredData)) {
    const paperData = structuredData.find(item => item['@type'] === 'ScholarlyArticle');
    if (paperData) {
      if (paperData.author && Array.isArray(paperData.author)) {
        paperAuthors = paperData.author.map((a: any) => a.name);
      }
      if (paperData.datePublished) {
        publicationYear = new Date(paperData.datePublished).getFullYear().toString();
      }
      if (paperData.publisher && paperData.publisher.name) {
        originalPublisher = paperData.publisher.name;
      }
    }
  }

  // Remove static/platform-injected meta tags that Helmet will manage
  useEffect(() => {
    const selectors = [
      // Canonical: critical to dedup. Lovable's SPA fallback may serve a
      // static `<link rel="canonical">` from index.html; without removal,
      // crawlers see two canonicals and may pick the wrong one.
      'link[rel="canonical"]:not([data-rh])',
      'meta[name="description"]:not([data-rh])',
      'meta[property="og:title"]:not([data-rh])',
      'meta[property="og:description"]:not([data-rh])',
      'meta[property="og:type"]:not([data-rh])',
      'meta[property="og:image"]:not([data-rh])',
      'meta[property="og:url"]:not([data-rh])',
      'meta[name="twitter:title"]:not([data-rh])',
      'meta[name="twitter:description"]:not([data-rh])',
      'meta[name="theme-color"]:not([data-rh])',
      'meta[name="msapplication-TileColor"]:not([data-rh])',
      'meta[name="application-name"]:not([data-rh])',
    ];
    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => el.remove());
    });
  }, []);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={normalizedCanonicalUrl} />

      {/* Per-page robots directive — overrides the default index,follow from index.html */}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {noindex && <meta name="googlebot" content="noindex,nofollow" />}

      {/* Pagination meta tags */}
      {nextPageUrl && <link rel="next" href={nextPageUrl} />}
      {prevPageUrl && <link rel="prev" href={prevPageUrl} />}

      {/* Academic citation meta tags for Google Scholar and rich snippets */}
      {isAcademicPaper && paperAuthors.length > 0 && (
        <>
          {paperAuthors.map((authorName, index) => (
            <meta key={index} name="citation_author" content={authorName} />
          ))}
          {publicationYear && (
            <meta name="citation_publication_date" content={publicationYear} />
          )}
          {originalPublisher && (
            <meta name="citation_publisher" content={originalPublisher} />
          )}
        </>
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={resolvedOgImage} />
      <meta property="og:url" content={normalizedCanonicalUrl} />
      <meta property="og:site_name" content="PaperVerse Reviews" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={resolvedOgImage} />
      <meta name="twitter:domain" content={SITE_DOMAIN} />
      <meta name="twitter:site" content="@paperverse" />

      {/* Additional paper review specific meta tags */}
      <meta name="topic" content="paper review, research collaboration" />
      <meta name="subject" content="Research Papers, Academic Review, Paper Discussion" />
      <meta name="classification" content="Research Tool, Review Service, Academic Platform" />

      {/* Enhanced meta tags for Google understanding */}
      <meta name="application-name" content="PaperVerse Reviews" />
      <meta name="msapplication-TileColor" content="#2563eb" />
      <meta name="theme-color" content="#2563eb" />

      {/* Structured Data / JSON-LD */}
      {structuredData && (
        Array.isArray(structuredData) ? (
          structuredData.map((data, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(data)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )
      )}
    </Helmet>
  );
};

export default SEOHead;
